import React, { useCallback } from 'react';
import { SHAPES, getDeviceFrame } from './shapes';
import { useBuilderDevice } from './DeviceContext';

/**
 * WBImage — Iter 151.
 *
 * A non-destructive image renderer used across the website-builder canvas.
 * It separates the FRAME (clip-path, border, radius) from the CONTENT
 * (object-fit, scale, offset) so users can switch shape, fit, or position
 * without ever modifying the underlying file.
 *
 * Double-click anywhere on the image to open the FloatingImageToolbar.
 */
export default function WBImage({
  src,
  alt = '',
  blockId,
  field = 'image_url',
  frame: rawFrame = {},
  className = '',
  style = {},
  imgStyle = {},
  imgClassName = 'w-full h-full',
}) {
  // Resolve the active device → flat frame (Phase 4 responsive).
  const { device } = useBuilderDevice();
  const frame = getDeviceFrame(rawFrame, device);
  const shape       = frame.shape || 'rectangle';
  const displayMode = frame.displayMode || 'fill';
  const radius      = frame.radius;
  const borderW     = frame.borderWidth || 0;
  const borderColor = frame.borderColor || '#FFFFFF';
  const borderStyle = frame.borderStyle || 'solid';
  const opacity     = frame.opacity != null ? frame.opacity : 1;
  const scale       = frame.scale || 1;
  const offX        = frame.offsetX || 0;
  const offY        = frame.offsetY || 0;
  const rotation    = frame.rotation || 0;

  const clipPath = SHAPES[shape]?.clipPath || 'none';
  const objectFit = displayMode === 'fit' ? 'contain' : displayMode === 'stretch' ? 'fill' : 'cover';
  const hasTransform = scale !== 1 || offX || offY || rotation;
  const transform = hasTransform
    ? `translate(${offX}px, ${offY}px) scale(${scale}) rotate(${rotation}deg)`
    : undefined;

  const handleDouble = useCallback((e) => {
    if (!blockId || !field) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent('wb:edit-image', {
      detail: {
        blockId,
        field,
        rect: { top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right, width: rect.width, height: rect.height },
      },
    }));
  }, [blockId, field]);

  // Resolve relative URLs that come back from the media-upload endpoint as `/uploads/...`
  const resolved = typeof src === 'string' && src.startsWith('/')
    ? `${process.env.REACT_APP_BACKEND_URL}${src}`
    : src;

  return (
    <div
      className={`relative ${className}`}
      data-wb-image={`${blockId || ''}/${field || ''}`}
      style={{
        clipPath,
        borderRadius: radius != null ? `${radius}px` : undefined,
        border: borderW ? `${borderW}px ${borderStyle} ${borderColor}` : undefined,
        opacity,
        overflow: 'hidden',
        ...style,
      }}
      onDoubleClick={handleDouble}
    >
      <img
        src={resolved}
        alt={alt}
        draggable={false}
        className={imgClassName}
        style={{
          objectFit,
          transform,
          width: '100%',
          height: '100%',
          ...imgStyle,
        }}
      />
    </div>
  );
}
